/**
 * The wire contract between the SPA and this app's Worker.
 *
 * In yominoki this boundary did not exist: the drill, n5, fluency, etymology
 * and atlas surfaces were Next server actions, so the "request body" was
 * whatever the function signature said and nothing validated it — the RPC
 * frame was the framework's. Converting to a Vite SPA + a Hono Worker makes
 * the boundary real, and a real boundary needs a schema on the server side of
 * it. Every schema here is the shape ONE endpoint accepts; the response types
 * beside them are what that endpoint resolves to.
 *
 * Imported by both halves on purpose. ../lib/api.ts posts against these
 * schemas and reads these types; ../../worker/routes/* parses against the same
 * objects. A field renamed on one side stops compiling on the other, which is
 * the property the server-action version got from the type checker for free
 * and would otherwise have lost in the conversion.
 *
 * The DB row types (`DrillCandidate`, `SentenceWithPost`, `AtlasEntry`, …) are
 * imported as TYPES ONLY. They are erased at build time, so nothing in
 * @bindery/yominoki-db — least of all `pg` — reaches the browser bundle.
 */
import * as z from "zod";
import type { EtymologyData, ReadingViewData } from "@bindery/ja-core";
import type { LearningPosition } from "@bindery/shared/contracts/learning";
import type { DrillCandidate } from "@bindery/yominoki-db/queries/drill";
import type { FluencyEventResult } from "@bindery/yominoki-db/queries/fluency";
import type {
  AtlasEntry,
  SentenceWithPost,
  TreemapEntry,
} from "@bindery/yominoki-db/queries/treemap";
import {
  zLearningPosition,
  zResponseId,
  zTreatmentId,
} from "@bindery/ja-core/schemas";

export type { AtlasEntry, DrillCandidate, SentenceWithPost, TreemapEntry };

// ------------------------------------------------------------------ limits

/**
 * Size caps on every client-supplied string and array.
 *
 * Over HTTP these endpoints are an open, scriptable surface for any Bluesky
 * account, and several of them turn input into model calls: `/api/drill/select`
 * makes one Haiku gloss call per unknown token, `/api/drill/grade` sends
 * `reference` and `attempt` to Sonnet, `/api/etymology` makes one provider call
 * per kanji in `display`. An uncapped array or string is uncapped spend. The
 * caps are generous against real input — a sentence comes from a Bluesky post,
 * which is at most 300 graphemes — and exist to bound the worst case, not to
 * shape the normal one. Exported so a test can assert against the same numbers.
 */
export const LIMITS = {
  /** One sentence of a ≤300-grapheme post, with room for surrogate pairs. */
  sentenceChars: 1000,
  /** A reader's attempt at translating that sentence. */
  attemptChars: 1000,
  /** The reference: a sentence, or its English translation. */
  referenceChars: 2000,
  /** Tokens in one sentence, and so lemmas derived from one sentence. */
  tokens: 300,
  /** One token's surface, POS or dictionary form; long enough for a URL token. */
  tokenChars: 256,
  /** The lemmas an N5 territory spans: one batch is 32 tiles. */
  territory: 256,
  /** A lemma whose kanji the etymology footnote resolves. */
  etymologyDisplayChars: 64,
  /** An at:// URI and a CID, as Bluesky mints them, with headroom. */
  sourceUriChars: 1024,
  sourceCidChars: 256,
  /** A uuid, or the n5 `<userId>::<day>::<map>` batch id. */
  sessionIdChars: 256,
} as const;

const tokenString = z.string().max(LIMITS.tokenChars);

/** Which way a drill round is translated. */
export const directionSchema = z.enum(["ja_to_en", "en_to_ja"]);
export type Direction = z.infer<typeof directionSchema>;

// ---------------------------------------------------------------- session

/**
 * Who the browser is. The SPA cannot read the `__Host-yominoki_session` cookie —
 * it is HttpOnly, and it is encrypted besides — so this endpoint is how a
 * client-rendered app learns what a server component used to just know.
 * `userId` is the internal users.id the DID maps to; it is null for a valid
 * cookie whose DID has no row, which is a state sign-in cannot produce but
 * a deleted user can.
 */
export interface SessionResponse {
  did: string | null;
  userId: string | null;
}

// ------------------------------------------------------------------ drill

export interface PickerSlotPayload {
  candidate: DrillCandidate;
  translation: string | null;
}

/** Three picker positions; a null slot is a position with nothing to offer. */
export type PickerSlot = PickerSlotPayload | null;

export const drillCandidatesRequest = z.object({
  direction: directionSchema,
});

export interface DrillCandidatesResponse {
  slots: PickerSlot[];
}

/**
 * The token triple `selectDrillPost` needs. Deliberately not the whole
 * `sentence_tokens` row shape: the endpoint reads exactly these three fields,
 * so accepting more would be accepting input nothing checks.
 */
export const drillTokenSchema = z.object({
  surface: tokenString.min(1),
  pos: tokenString,
  dictionary_form: tokenString,
});

export const drillSelectRequest = z.object({
  sentenceId: z.uuid(),
  readingId: z.uuid(),
  sentenceText: z.string().min(1).max(LIMITS.sentenceChars),
  tokens: z.array(drillTokenSchema).max(LIMITS.tokens),
});

export interface DrillSelection {
  sentence: string;
  translation: string;
  glosses: { surface: string; pos: string; gloss: string }[];
  stretchLemmas: string[];
}

/**
 * The grader's output shape, and the ONE place it is written down. The Worker
 * hands this same object to `generateObject` as the structured-output schema,
 * so the model's contract and the wire contract cannot drift apart.
 */
export const gradeAxesSchema = z.object({
  overall: z.enum(["match", "valid_alternative", "wrong"]),
  lemma: z.string().describe("Assessment of vocabulary/lemma usage"),
  particles: z.string().describe("Assessment of particle usage"),
  order: z.string().describe("Assessment of word order"),
  register: z.string().describe("Assessment of register/formality"),
  negation: z.string().describe("Assessment of negation usage"),
  comment: z.string().describe("Brief overall comment on the attempt"),
});
export type GradeAxes = z.infer<typeof gradeAxesSchema>;

export const drillGradeRequest = z.object({
  sentenceId: z.uuid(),
  direction: directionSchema,
  attempt: z.string().min(1).max(LIMITS.attemptChars),
  reference: z.string().min(1).max(LIMITS.referenceChars),
  focusLemmas: z.array(tokenString).max(LIMITS.tokens),
});

/**
 * `grade` is narrowed to the one field the endpoint reads. The client still
 * posts the whole `GradeAxes` it got from `/api/drill/grade` (zod strips the
 * rest), but the free-text axes are never stored, so accepting them would be
 * accepting unbounded input nothing uses.
 */
export const drillOutcomeRequest = z.object({
  sentenceId: z.uuid(),
  readingId: z.uuid(),
  direction: directionSchema,
  stretchLemmas: z.array(tokenString).max(LIMITS.tokens),
  grade: z.object({ overall: gradeAxesSchema.shape.overall }),
});

export const engagementSchema = z.discriminatedUnion("kind", [
  // `z.literal([1, -1])` rather than a union of two literals: the union form
  // trips the browser-compat lint, which reads `.union(` as
  // `Set.prototype.union`. Same type, no false positive.
  z.object({ kind: z.literal("vote"), value: z.literal([1, -1]) }),
  z.object({ kind: z.literal("like") }),
  z.object({ kind: z.literal("bookmark") }),
  z.object({ kind: z.literal("dismiss") }),
]);
export type EngagementKind = z.infer<typeof engagementSchema>;

export const drillEngagementRequest = z.object({
  readingId: z.uuid(),
  sourceUri: z.string().max(LIMITS.sourceUriChars).nullable(),
  sourceCid: z.string().max(LIMITS.sourceCidChars).nullable(),
  engagement: engagementSchema,
});

// --------------------------------------------------------------------- n5

/**
 * Everything the N5 route's server component used to compute before it
 * rendered: the batch of tiles for this (user, day, map number), and the
 * sentences for every visible lemma so a tile click paints instantly.
 *
 * It is one endpoint rather than one-per-piece because the pieces are not
 * independent — `preloadedSentences` is scored against the territory the
 * batch defines, so a client that fetched them separately could preload
 * against a batch it no longer has.
 */
export interface N5BatchResponse {
  batchId: string;
  mapNumber: number;
  entries: TreemapEntry[];
  /** Keyed by dictionary_form. Empty for a signed-out visitor, by design. */
  preloadedSentences: Record<string, SentenceWithPost[]>;
}

export const n5SentencesRequest = z.object({
  dictionaryForm: tokenString.min(1),
  territory: z.array(tokenString).max(LIMITS.territory),
  medianFreq: z.number(),
});

export interface N5SentencesResponse {
  sentences: SentenceWithPost[];
}

// ---------------------------------------------------------------- fluency

/**
 * `sessionId` is a plain non-empty string, NOT a uuid, and that is deliberate.
 * The n5 territory passes its batchId — `<userId>::<day>::<map>` — as the
 * session identifier, which is what yominoki did and what the learning-record
 * rows already contain. Tightening it to `z.uuid()` here would reject the
 * surface that produces most of these events.
 */
export const fluencyEventRequest = z.object({
  surface: tokenString.min(1),
  response: zResponseId,
  treatmentId: zTreatmentId,
  sessionId: z.string().min(1).max(LIMITS.sessionIdChars),
  position: zLearningPosition.optional(),
  capStrongPromotion: z.boolean().optional(),
});

export type FluencyEventBody = z.infer<typeof fluencyEventRequest>;
export type { FluencyEventResult, LearningPosition };

export type FluencyEventResponse =
  | { ok: true; result: FluencyEventResult }
  | { ok: false; reason: "unauth" };

// -------------------------------------------------------------- etymology

export const etymologyRequest = z.object({
  display: z.string().min(1).max(LIMITS.etymologyDisplayChars),
});

export interface EtymologyResponse {
  entries: { kanji: string; data: EtymologyData }[];
}

// ------------------------------------------------------------------ atlas

export const ATLAS_SLUGS = ["n1", "n2", "n3", "n4", "n5"] as const;
export type AtlasSlug = (typeof ATLAS_SLUGS)[number];

/** n1 is JLPT level 1 and the hardest; the slug number IS the level. */
export const SLUG_TO_LEVEL = {
  n1: 1,
  n2: 2,
  n3: 3,
  n4: 4,
  n5: 5,
} as const satisfies Record<AtlasSlug, 1 | 2 | 3 | 4 | 5>;

export function isAtlasSlug(value: string): value is AtlasSlug {
  return value in SLUG_TO_LEVEL;
}

export interface AtlasResponse {
  entries: AtlasEntry[];
}

// ---------------------------------------------------------------- reading

export type ReadingResponse = ReadingViewData;

// ----------------------------------------------------------------- errors

/** Every non-2xx JSON body this Worker produces. */
export interface ApiError {
  error: string;
  description?: string;
}
